import { supabaseClient } from '../config/supabase'

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000'

async function getToken(): Promise<string | null> {
  const { data: { session } } = await supabaseClient.auth.getSession()
  return session?.access_token ?? null
}

/**
 * Fire-and-forget email notification for hired/rejected status changes.
 * Errors are swallowed — a failed email never breaks the UI action.
 */
export async function notifyStatusEmail(appId: string, status: 'hired' | 'rejected'): Promise<void> {
  try {
    const token = await getToken()
    if (!token) return

    await fetch(`${API_URL}/api/emails/notify`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({ appId, status }),
    })
  } catch (err) {
    console.warn('[emailNotify] Failed to send notification:', err)
  }
}

/**
 * Fire-and-forget email notification to the employer when an alumni applies.
 */
export async function notifyNewApplicationEmail(jobId: string): Promise<void> {
  try {
    const token = await getToken()
    if (!token) return

    await fetch(`${API_URL}/api/emails/notify`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({ jobId, status: 'applied' }),
    })
  } catch (err) {
    console.warn('[emailNotify] Failed to send new application notification:', err)
  }
}

/**
 * Fire-and-forget email notification to an alumni when invited to apply for a job.
 */
export async function notifyJobInvitationEmail(alumniId: string, jobId: string): Promise<void> {
  try {
    const token = await getToken()
    if (!token) return

    await fetch(`${API_URL}/api/emails/notify`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({ alumniId, jobId, status: 'invited' }),
    })
  } catch (err) {
    console.warn('[emailNotify] Failed to send job invitation notification:', err)
  }
}

/**
 * Fire-and-forget email to an alumni when their registration is approved.
 */
export async function notifyAlumniApprovedEmail(alumniUserId: string): Promise<void> {
  try {
    const token = await getToken()
    if (!token) return

    await fetch(`${API_URL}/api/emails/notify`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({ alumniUserId, status: 'alumni_approved' }),
    })
  } catch (err) {
    console.warn('[emailNotify] Failed to send alumni approved notification:', err)
  }
}

/**
 * Fire-and-forget email to an alumni when their registration is rejected.
 */
export async function notifyAlumniRejectedEmail(alumniUserId: string, reason: string): Promise<void> {
  try {
    const token = await getToken()
    if (!token) return

    await fetch(`${API_URL}/api/emails/notify`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({ alumniUserId, reason, status: 'alumni_rejected' }),
    })
  } catch (err) {
    console.warn('[emailNotify] Failed to send alumni rejected notification:', err)
  }
}

/**
 * Fire-and-forget email to an employer when their partnership request is approved.
 */
export async function notifyPartnershipApprovedEmail(companyId: string, departmentName: string): Promise<void> {
  try {
    const token = await getToken()
    if (!token) return

    await fetch(`${API_URL}/api/emails/notify`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({ companyId, departmentName, status: 'partnership_approved' }),
    })
  } catch (err) {
    console.warn('[emailNotify] Failed to send partnership approved notification:', err)
  }
}

/**
 * Send a login OTP code to the user's email address.
 * Called immediately after generating the OTP via the generate_login_otp() RPC.
 */
export async function sendLoginOtpEmail(
  recipientEmail: string,
  name: string,
  code: string,
): Promise<void> {
  try {
    const token = await getToken()
    if (!token) return

    await fetch(`${API_URL}/api/emails/notify`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({ email: recipientEmail, name, code, status: 'login_otp' }),
    })
  } catch (err) {
    console.warn('[emailNotify] Failed to send OTP email:', err)
  }
}

/**
 * Fire-and-forget email to an employer when their partnership request is rejected.
 */
export async function notifyPartnershipRejectedEmail(companyId: string, departmentName: string): Promise<void> {
  try {
    const token = await getToken()
    if (!token) return

    await fetch(`${API_URL}/api/emails/notify`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({ companyId, departmentName, status: 'partnership_rejected' }),
    })
  } catch (err) {
    console.warn('[emailNotify] Failed to send partnership rejected notification:', err)
  }
}
